// ============================================================================
// ProfileTabNav (L16, F-828/F-829 + M-802) — onglets `?tab=` du profil
// ============================================================================
// Portage de components/profile/profile-tab-nav.tsx. Le PoC s'appuyait sur un
// TabNav maison (hors lot) ; @gs/ui n'a pas de primitive d'onglets et packages/ui
// est INTERDIT a ce frag -> nav locale batie sur les tokens (regle #28, zero CSS
// en dur). Meme semantique : liens reels (deep-linkables), pas un state client.
//
// F-828 : la nav ENTIERE disparait si <= 1 onglet disponible (un employee qui
// regarde un tiers n'a que « apercu » -> aucune nav, pas un onglet solitaire).
// F-829 : `hrefFor` preserve `extraQuery` (from/fromTeam) et OMET `tab=` pour
// « apercu » (l'URL canonique de l'apercu est nue).
// M-802 : `resolveProfileTab` fait retomber SILENCIEUSEMENT un `?tab=` non
// autorise sur « apercu » — aucune erreur affichee. La liste `available` vient du
// SERVEUR (jamais recalculee ici).

import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import type { ProfileTab } from '../../hooks/useProfile';

/** Ordre canonique des onglets (miroir de PROFILE_TABS du PoC). */
export const PROFILE_TABS: readonly ProfileTab[] = [
  'apercu', 'pouls', 'objectifs', 'documents', 'remuneration', 'gestion',
] as const;

/**
 * M-802 : `raw` absent/invalide/non autorise -> « apercu », silencieusement.
 * Fonction PURE (testee) — la garde reelle reste serveur.
 */
export function resolveProfileTab(raw: string | null | undefined, available: ProfileTab[]): ProfileTab {
  return available.find((t) => t === raw) ?? 'apercu';
}

export interface ProfileTabNavProps {
  basePath: string;
  active: ProfileTab;
  available: ProfileTab[];
  /** F-829 : params de contexte a preserver en changeant d'onglet (from/fromTeam). */
  extraQuery?: Record<string, string>;
}

export function ProfileTabNav({ basePath, active, available, extraQuery }: ProfileTabNavProps) {
  const { t } = useTranslation();

  // F-828 : <= 1 onglet -> aucune nav du tout.
  if (available.length <= 1) return null;

  const hrefFor = (tab: ProfileTab) => {
    const query = new URLSearchParams(extraQuery);
    if (tab !== 'apercu') query.set('tab', tab);
    const qs = query.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  };

  return (
    <nav
      aria-label={t('gest:profile.tabs.navLabel')}
      className="-mb-px flex gap-1 overflow-x-auto border-b border-slate-200 dark:border-slate-700"
    >
      {available.map((tab) => {
        const isActive = tab === active;
        return (
          <Link
            key={tab}
            to={hrefFor(tab)}
            aria-current={isActive ? 'page' : undefined}
            className={`touch-manipulation whitespace-nowrap border-b-2 px-3 py-2 text-field font-medium transition-colors ${
              isActive
                ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            {t(`gest:profile.tabs.${tab}`)}
          </Link>
        );
      })}
    </nav>
  );
}
